import React, { useState } from "react";
import { adminApi, formatApiError } from "../../context/AuthContext";
import { Button } from "../ui/button";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger,
} from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Textarea } from "../ui/textarea";
import { toast } from "sonner";
import { MessageCircle, ExternalLink } from "lucide-react";

export default function WhatsAppTemplateButton({ customerId, quoteId, pickupId, label = "WhatsApp bericht maken", testIdPrefix = "wa" }) {
  const [open, setOpen] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [preview, setPreview] = useState("");
  const [waUrl, setWaUrl] = useState("");
  const [phone, setPhone] = useState("");
  const [error, setError] = useState("");

  const loadTemplates = async () => {
    try {
      const r = await adminApi.get("/admin/whatsapp/templates");
      setTemplates(r.data.templates || []);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const onOpenChange = (o) => {
    setOpen(o);
    if (o) {
      setError(""); setPreview(""); setWaUrl(""); setSelectedId("");
      loadTemplates();
    }
  };

  const onGenerate = async (templateId) => {
    setSelectedId(templateId);
    setError(""); setPreview(""); setWaUrl("");
    try {
      const r = await adminApi.post("/admin/whatsapp/generate", {
        template_id: templateId,
        customer_id: customerId || null,
        quote_id: quoteId || null,
        pickup_id: pickupId || null,
      });
      setPreview(r.data.preview);
      setWaUrl(r.data.url);
      setPhone(r.data.phone);
    } catch (e) {
      setError(formatApiError(e));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="rounded-none border-[#25D366] text-[#25D366] hover:bg-[#25D366] hover:text-white" data-testid={`${testIdPrefix}-open-button`}>
          <MessageCircle className="w-4 h-4 mr-1" /> {label}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>WhatsApp bericht klaarzetten</DialogTitle>
          <DialogDescription>
            Kies een template en pas het voorbeeld aan voor je in WhatsApp opent. Het bericht wordt nooit automatisch verzonden.
          </DialogDescription>
        </DialogHeader>

        <div>
          <label className="text-xs uppercase tracking-wide font-bold">Kies template</label>
          <Select value={selectedId} onValueChange={onGenerate}>
            <SelectTrigger className="rounded-none mt-1" data-testid={`${testIdPrefix}-template-select`}>
              <SelectValue placeholder="Kies een template..." />
            </SelectTrigger>
            <SelectContent>
              {templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 text-sm" data-testid={`${testIdPrefix}-error`}>
            {error}
          </div>
        )}

        {preview && (
          <>
            <div>
              <label className="text-xs uppercase tracking-wide font-bold">Voorbeeld (kun je hier nog aanpassen vóór verzenden)</label>
              <Textarea
                rows={9} value={preview} onChange={(e) => setPreview(e.target.value)}
                className="rounded-none mt-1 font-mono text-xs"
                data-testid={`${testIdPrefix}-preview-textarea`}
              />
              <div className="text-[10px] text-gray-500 mt-1">
                Naar nummer: <span className="font-mono">+{phone}</span>
              </div>
            </div>
            <a
              href={`https://wa.me/${phone}?text=${encodeURIComponent(preview)}`}
              target="_blank" rel="noopener noreferrer"
              data-testid={`${testIdPrefix}-open-link`}
              className="inline-flex items-center justify-center bg-[#25D366] hover:bg-[#1fb858] text-white font-chivo font-bold uppercase text-xs px-4 py-3"
            >
              <ExternalLink className="w-4 h-4 mr-2" /> Open in WhatsApp
            </a>
            <p className="text-xs text-gray-500">
              Het bericht wordt niet automatisch verzonden — controleer en verstuur zelf in WhatsApp.
            </p>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
